'use client';

import { FC, useState, useContext, useMemo } from 'react';
import {
  Dialog,
  Flex,
  ButtonGroup,
  Text,
  Input,
  Button,
  Alert,
  Spacer,
} from '@/_components';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ContactMethodDialogProps } from './ContactMethodDialog.types';
import { updateUser } from '@/_api/users';
import { ToastContext } from '@/_contexts/ToastProvider';
import { setCookie } from 'cookies-next/client';
import {
  CONTACT_METHOD_PREFERENCE_COOKIE,
  MAX_COOKIE_AGE,
} from '@/_constants/cookies';
import { deformatPhoneNumber } from '@/_utils/phoneNumbers';

export const ContactMethodDialog: FC<ContactMethodDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { firm } = useContext(FirmSettingsContext);
  const { user, token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [isLoading, setIsLoading] = useState(false);

  const alertText: string = useMemo(() => {
    return (
      firm?.preferences?.custom_welcome_message ||
      ''
        .replace(/{client_fname}/g, user?.fname || '')
        .replace(/{client_lname}/g, user?.lname || '')
        .replace(/{client_preferred_name}/g, user?.preferred_name || '')
        .replace(/{client_full_name}/g, user?.full_name || '')
    );
  }, [
    firm?.preferences?.custom_welcome_message,
    user?.fname,
    user?.lname,
    user?.preferred_name,
    user?.full_name,
  ]);

  // Only show dialog if user is not an advisor
  if (!user || user.is_advisor) {
    return null;
  }

  const handleSubmit = async () => {
    if (!user || !token) return;

    try {
      setIsLoading(true);
      const response = await updateUser({
        userId: user.id,
        token,
        phone_number: deformatPhoneNumber(phoneNumber),
        preferred_contact_method: 'sms',
      });

      if (response.error) {
        addToast('error', 'Something went wrong while saving progress', {
          error: response.error,
        });
        console.error('Error updating user:', response.error);
      } else {
        setCookie(CONTACT_METHOD_PREFERENCE_COOKIE(user.id), 'sms', {
          maxAge: MAX_COOKIE_AGE,
        });
        addToast('success', 'Successfully Updated!');
        onClose();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      addToast('error', 'Something went wrong while saving progress', {
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!user || !token) return;

    try {
      setIsLoading(true);
      await updateUser({
        userId: user.id,
        token,
        preferred_contact_method: 'email',
      });
      setCookie(CONTACT_METHOD_PREFERENCE_COOKIE(user.id), 'email', {
        maxAge: MAX_COOKIE_AGE,
      });
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
      addToast('error', 'Something went wrong while saving progress', {
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="" isPersistent={true}>
      <Dialog.Content>
        {firm?.preferences?.custom_welcome_message && (
          <Alert type="info" icon="info">
            {alertText}
          </Alert>
        )}

        <Spacer gap={16} />

        <Flex isColumn={true} gap={10}>
          <Flex isColumn={true} gap={2}>
            <Text type="div" fontWeight="bold">
              Don&apos;t want to miss any notifications?
            </Text>
            <Text>Let us remind you via SMS.</Text>
          </Flex>

          <Flex isColumn={true} gap={4}>
            <Flex isColumn={true} gap={2}>
              <Input
                label="Your Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                mask="(___) ___-____"
                placeholder="(123) 456-7890"
              />
              <Text type="tiny">* Msg & data rates may apply.</Text>
            </Flex>
          </Flex>

          <Alert type="info">
            By providing this phone number, you agree to receive messages from{' '}
            {firm?.name}. Message and data rates may apply. Message frequency
            varies.
          </Alert>
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="secondary"
            onClick={handleSkip}
            isDisabled={isLoading}
            value="Skip"
          />
          <Button
            intent="primary"
            onClick={handleSubmit}
            isDisabled={!phoneNumber || isLoading}
            value="Submit"
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
